import React, { useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Stack,
  Group,
  Text,
  Table,
  Card,
  ActionIcon,
  Badge,
  Tabs,
  ThemeIcon,
  Tooltip,
  Paper,
  SegmentedControl,
  Accordion
} from '@mantine/core';
import {
  IconEdit,
  IconTrash,
  IconCheck,
  IconX,
  IconChecklist,
  IconShieldCheck,
  IconInfoCircle
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';

import ControlEvaluationForm from '../ControlEvaluation';
import AIScoreExplanationModal from '../canvas/components/AIScoreExplanationModal';
import PolicyEvidenceBadges from '../components/PolicyEvidenceBadges';
import { calculateMitigationQualityScore } from '../utils/aiScoreCalculators';
import {
  setSavedControls,
  fetchSavedControlEvaluations,
  editSavedActionPlanRow,
  discardSavedControl
} from '../redux/actions/controlActions';

const riskPriorityColors = {
  Critical: '#1A1A1A',
  High: '#993333',
  Significant: '#B94D4D',
  Moderate: '#D4AA7D',
  Low: '#B8C2B7',
  Negligible: '#D8D8D8',
  None: '#EBEBEB'
};

/**
 * ActionPlan - Display and manage saved mitigation controls
 * Shows controls generated and saved during autopilot assessment
 */
function ActionPlan({ riskFormData, modelId, assessmentUuid }) {
  const dispatch = useDispatch();

  // Redux state - residualRiskData scoped by assessmentUuid
  const savedControls = useSelector(s => s.controls.savedControls || {});
  const residualRiskData = useSelector(s =>
    s.residualRisk?.byModel?.[assessmentUuid] || s.residualRisk.residualRiskData || {}
  );
  const savedEvaluations = useSelector(s => s.controls.savedControlEvaluations || {});

  // Local state
  const [editId, setEditId] = useState(null);
  const [editTxt, setEditTxt] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [expandedTabs, setExpandedTabs] = useState({});
  const [explanationModal, setExplanationModal] = useState({ opened: false, scoreType: null, scoreData: null });
  const [viewMode, setViewMode] = useState('actions'); // 'actions' or 'risks'
  const [evalFilter, setEvalFilter] = useState('all'); // 'all', 'evaluated', 'not-evaluated'

  // Bootstrap saved controls and evaluations
  useEffect(() => {
    if (modelId && assessmentUuid) {
      dispatch(setSavedControls(modelId, assessmentUuid));
      dispatch(fetchSavedControlEvaluations(modelId, assessmentUuid));
    }
  }, [modelId, assessmentUuid, dispatch]);

  // Flatten all risks for lookup
  const allRisks = useMemo(() => {
    return Object.values(riskFormData || {})
      .flatMap(sec => Object.values(sec.areaRisks || {}))
      .flat()
      .filter(r => r.status === 'Saved');
  }, [riskFormData]);

  const findRisk = rid => allRisks.find(r => r.riskId === rid);

  // Convert savedControls to flat array for display, filtered by modelId
  const controlsList = useMemo(() => {
    const controls = [];
    const seen = new Set(); // Prevent duplicates

    Object.entries(savedControls).forEach(([riskId, controlArray]) => {
      if (Array.isArray(controlArray)) {
        controlArray.forEach(ctrl => {
          // IMPORTANT: Filter by modelId AND assessmentUuid to prevent showing controls from other assessments
          if (ctrl.modelId !== modelId) return;
          if (ctrl.assessmentUuid && ctrl.assessmentUuid !== assessmentUuid) return;

          // Prevent duplicates (same control might be in multiple risk buckets)
          if (seen.has(ctrl.controlId)) return;

          // DEBUG: Log policy evidence
          if (ctrl.policyEvidence && ctrl.policyEvidence.length > 0) {
            console.log('[ActionPlan] Control with policy evidence:', {
              name: ctrl.name?.substring(0, 50),
              controlId: ctrl.controlId,
              policyEvidenceLength: ctrl.policyEvidence.length,
              policyEvidenceType: typeof ctrl.policyEvidence,
              policyEvidenceSample: ctrl.policyEvidence[0]
            });
          }
          seen.add(ctrl.controlId);

          // Ensure we have riskIds array
          const riskIds = ctrl.riskIds || [riskId];
          controls.push({
            ...ctrl,
            riskIds
          });
        });
      }
    });
    return controls;
  }, [savedControls, modelId, assessmentUuid]);

  // Filter controls by evaluation status
  const filteredControls = useMemo(() => {
    if (evalFilter === 'all') return controlsList;

    return controlsList.filter(ctrl => {
      const primaryRiskId = ctrl.riskIds?.[0];
      const evalRow = primaryRiskId
        ? (savedEvaluations[primaryRiskId]?.[ctrl.controlId]?.[0] || {})
        : {};
      const evaluated = evalRow.control_recurrence || evalRow.executed_next || evalRow.self_evaluation;

      if (evalFilter === 'evaluated') return evaluated;
      if (evalFilter === 'not-evaluated') return !evaluated;
      return true;
    });
  }, [controlsList, evalFilter, savedEvaluations]);

  // Group controls by risk for "By Risk" view
  const controlsByRisk = useMemo(() => {
    const grouped = {};

    filteredControls.forEach(ctrl => {
      ctrl.riskIds.forEach(riskId => {
        if (!grouped[riskId]) {
          grouped[riskId] = [];
        }
        grouped[riskId].push(ctrl);
      });
    });

    return grouped;
  }, [filteredControls]);

  // Calculate mitigation efficiency score
  const mitigationScore = useMemo(() => {
    return calculateMitigationQualityScore(savedControls, residualRiskData, riskFormData);
  }, [savedControls, residualRiskData, riskFormData]);

  // Get badge color for efficiency level
  const getEfficiencyColor = (level) => {
    if (level === 'EFFECTIVE') return 'green';
    if (level === 'MODERATE') return 'yellow';
    if (level === 'WEAK') return 'red';
    return 'gray';
  };

  // Handlers
  const confirmEdit = (ctrl, newName) => {
    dispatch(editSavedActionPlanRow(ctrl.controlId, newName));
    notifications.show({
      title: 'Success',
      message: 'Action name updated',
      color: 'green'
    });
    setEditId(null);
  };

  const handleDelete = (ctrl) => {
    const primaryRiskId = ctrl.riskIds?.[0];
    if (primaryRiskId) {
      dispatch(discardSavedControl(ctrl, primaryRiskId));
      notifications.show({
        title: 'Deleted',
        message: 'Action removed from plan',
        color: 'blue'
      });
    }
  };

  const toggleRow = (id, targetTab) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id) && !targetTab) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

    if (targetTab) {
      setExpandedTabs(prev => ({ ...prev, [id]: targetTab }));
    }
  };

  const titleCase = str => str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : '';

  return (
    <Stack gap="lg" p="md">
      {/* Header with Mitigation Efficiency Badge */}
      <Group justify="space-between" align="center">
        <Group gap="md">
          <Text size="xl" fw={600}>Action Plan</Text>
          <Tooltip label="Click for AI mitigation quality analysis" withArrow>
            <Badge
              size="md"
              variant="light"
              color={getEfficiencyColor(mitigationScore.level)}
              leftSection={<IconShieldCheck size={14} />}
              style={{ cursor: 'pointer', textTransform: 'capitalize' }}
              onClick={() => setExplanationModal({
                opened: true,
                scoreType: 'mitigationQuality',
                scoreData: mitigationScore
              })}
            >
              {mitigationScore.level} ({mitigationScore.score}/100)
            </Badge>
          </Tooltip>
        </Group>

        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="blue">
            <IconChecklist size={14} />
          </ThemeIcon>
          <Text size="sm" c="dimmed">{filteredControls.length} Actions</Text>
        </Group>
      </Group>

      {/* View Toggle and Filters */}
      <Group justify="space-between" align="center">
        <SegmentedControl
          value={viewMode}
          onChange={setViewMode}
          data={[
            { label: 'By Action', value: 'actions' },
            { label: 'By Risk', value: 'risks' }
          ]}
          size="sm"
        />

        <SegmentedControl
          value={evalFilter}
          onChange={setEvalFilter}
          data={[
            { label: 'All', value: 'all' },
            { label: 'Evaluated', value: 'evaluated' },
            { label: 'Not Evaluated', value: 'not-evaluated' }
          ]}
          size="sm"
        />
      </Group>

      {/* Actions Table or Grouped View */}
      {viewMode === 'actions' ? (
        <Card withBorder radius="md" p={0}>
          <Table highlightOnHover>
            <Table.Thead style={{ backgroundColor: '#F9FAFB' }}>
              <Table.Tr>
                <Table.Th>Action Name</Table.Th>
                <Table.Th w={100}>Evaluated?</Table.Th>
                <Table.Th w={280}>Policy Coverage</Table.Th>
                <Table.Th w={100}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>

            <Table.Tbody>
              {filteredControls.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={4} align="center">
                    <Stack gap="xs" align="center" py="xl">
                      <IconChecklist size={48} color="gray" opacity={0.3} />
                      <Text c="dimmed">
                        {controlsList.length === 0
                          ? 'No actions yet. Run an autopilot assessment to generate actions.'
                          : 'No actions match the selected filter.'}
                      </Text>
                    </Stack>
                  </Table.Td>
                </Table.Tr>
              )}

              {filteredControls.map(ctrl => {
              const primaryRiskId = ctrl.riskIds?.[0];
              const evalRow = primaryRiskId
                ? (savedEvaluations[primaryRiskId]?.[ctrl.controlId]?.[0] || {})
                : {};
              const evaluated = evalRow.control_recurrence || evalRow.executed_next || evalRow.self_evaluation;

              // DEBUG: Log each control rendered in table
              console.log('[ActionPlan TABLE RENDER] Control:', {
                name: ctrl.name?.substring(0, 40),
                controlId: ctrl.controlId,
                hasPolicyEvidence: !!ctrl.policyEvidence,
                policyEvidenceLength: ctrl.policyEvidence?.length,
                policyEvidenceType: typeof ctrl.policyEvidence,
                policyEvidenceSample: ctrl.policyEvidence?.[0],
                allControlKeys: Object.keys(ctrl)
              });

              return (
                <React.Fragment key={ctrl.controlId}>
                  <Table.Tr
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleRow(ctrl.controlId)}
                  >
                    {/* Action Name */}
                    <Table.Td>
                      {editId === ctrl.controlId ? (
                        <Group gap="xs" onClick={e => e.stopPropagation()}>
                          <input
                            style={{ flex: 1, padding: '4px 8px', borderRadius: 4, border: '1px solid #ced4da' }}
                            value={editTxt}
                            onChange={e => setEditTxt(e.target.value)}
                            autoFocus
                          />
                          <ActionIcon
                            size="sm"
                            color="green"
                            onClick={() => confirmEdit(ctrl, editTxt.trim())}
                          >
                            <IconCheck size={16} />
                          </ActionIcon>
                          <ActionIcon
                            size="sm"
                            color="gray"
                            onClick={() => setEditId(null)}
                          >
                            <IconX size={16} />
                          </ActionIcon>
                        </Group>
                      ) : (
                        <Text size="sm">{ctrl.name}</Text>
                      )}
                    </Table.Td>

                    {/* Evaluated? */}
                    <Table.Td>
                      <ActionIcon
                        size="sm"
                        variant={evaluated ? 'filled' : 'subtle'}
                        color={evaluated ? 'green' : 'gray'}
                        onClick={e => {
                          e.stopPropagation();
                          const alreadyOpen = expandedRows.has(ctrl.controlId);
                          const onEvalTab = (expandedTabs[ctrl.controlId] ?? 'risks') === 'evaluation';
                          if (alreadyOpen && onEvalTab) {
                            toggleRow(ctrl.controlId);
                          } else {
                            toggleRow(ctrl.controlId, 'evaluation');
                          }
                        }}
                      >
                        {evaluated ? <IconCheck size={16} /> : <IconInfoCircle size={16} />}
                      </ActionIcon>
                    </Table.Td>

                    {/* Policy Coverage */}
                    <Table.Td>
                      {ctrl.policyEvidence && ctrl.policyEvidence.length > 0 ? (
                        <PolicyEvidenceBadges
                          policyEvidence={ctrl.policyEvidence}
                          compact={true}
                          expandable={false}
                        />
                      ) : (
                        <Badge color="gray" size="xs" variant="light">
                          No policy match
                        </Badge>
                      )}
                    </Table.Td>

                    {/* Actions: Edit/Delete */}
                    <Table.Td onClick={e => e.stopPropagation()}>
                      <Group gap="xs">
                        <ActionIcon
                          size="sm"
                          variant="subtle"
                          color="blue"
                          onClick={() => {
                            setEditId(ctrl.controlId);
                            setEditTxt(ctrl.name);
                          }}
                        >
                          <IconEdit size={16} />
                        </ActionIcon>
                        <ActionIcon
                          size="sm"
                          variant="subtle"
                          color="red"
                          onClick={() => handleDelete(ctrl)}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>

                  {/* Expanded Row with Tabs */}
                  {expandedRows.has(ctrl.controlId) && (
                    <Table.Tr>
                      <Table.Td colSpan={4} style={{ padding: 0, backgroundColor: '#F9FAFB' }}>
                        <Paper p="md">
                          <Tabs
                            value={expandedTabs[ctrl.controlId] || 'risks'}
                            onChange={(val) => setExpandedTabs(prev => ({ ...prev, [ctrl.controlId]: val }))}
                          >
                            <Tabs.List>
                              <Tabs.Tab value="risks">Risks ({ctrl.riskIds?.length || 0})</Tabs.Tab>
                              <Tabs.Tab value="evaluation">Evaluation</Tabs.Tab>
                            </Tabs.List>

                            <Tabs.Panel value="risks" pt="md">
                              <Table striped withTableBorder>
                                <Table.Thead>
                                  <Table.Tr>
                                    <Table.Th>Risk Name</Table.Th>
                                    <Table.Th>Source</Table.Th>
                                    <Table.Th>Priority</Table.Th>
                                  </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                  {(ctrl.riskIds || []).map(rid => {
                                    const r = findRisk(rid) || {};
                                    return (
                                      <Table.Tr key={rid}>
                                        <Table.Td>{r.riskName || rid}</Table.Td>
                                        <Table.Td>{r.category || '—'}</Table.Td>
                                        <Table.Td>
                                          <Badge
                                            size="sm"
                                            style={{
                                              backgroundColor: riskPriorityColors[titleCase(r.priority)] || riskPriorityColors.None,
                                              color: '#fff'
                                            }}
                                          >
                                            {titleCase(r.priority)}
                                          </Badge>
                                        </Table.Td>
                                      </Table.Tr>
                                    );
                                  })}
                                </Table.Tbody>
                              </Table>
                            </Tabs.Panel>

                            <Tabs.Panel value="evaluation" pt="md">
                              <ControlEvaluationForm
                                riskId={ctrl.riskIds?.[0]}
                                controlId={ctrl.controlId}
                                modelId={modelId}
                                assessmentUuid={assessmentUuid}
                              />
                            </Tabs.Panel>
                          </Tabs>
                        </Paper>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </React.Fragment>
              );
            })}
          </Table.Tbody>
        </Table>
      </Card>
      ) : (
        /* By Risk Grouped View */
        <Stack gap="md">
          {Object.keys(controlsByRisk).length === 0 ? (
            <Card withBorder radius="md" p="xl">
              <Stack gap="xs" align="center">
                <IconChecklist size={48} color="gray" opacity={0.3} />
                <Text c="dimmed">
                  {controlsList.length === 0
                    ? 'No actions yet. Run an autopilot assessment to generate actions.'
                    : 'No actions match the selected filter.'}
                </Text>
              </Stack>
            </Card>
          ) : (
            <Accordion variant="separated" radius="md">
              {Object.entries(controlsByRisk).map(([riskId, controls]) => {
                const risk = findRisk(riskId) || {};
                return (
                  <Accordion.Item key={riskId} value={riskId}>
                    <Accordion.Control>
                      <Group justify="space-between" wrap="nowrap">
                        <Stack gap={4}>
                          <Text fw={600} size="sm">{risk.riskName || riskId}</Text>
                          <Text size="xs" c="dimmed">{risk.category || '—'}</Text>
                        </Stack>
                        <Group gap="xs">
                          <Badge
                            size="sm"
                            style={{
                              backgroundColor: riskPriorityColors[titleCase(risk.priority)] || riskPriorityColors.None,
                              color: '#fff'
                            }}
                          >
                            {titleCase(risk.priority)}
                          </Badge>
                          <Badge size="sm" variant="light" color="blue">
                            {controls.length} {controls.length === 1 ? 'Action' : 'Actions'}
                          </Badge>
                        </Group>
                      </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Stack gap="sm">
                        {controls.map(ctrl => {
                          const primaryRiskId = ctrl.riskIds?.[0];
                          const evalRow = primaryRiskId
                            ? (savedEvaluations[primaryRiskId]?.[ctrl.controlId]?.[0] || {})
                            : {};
                          const evaluated = evalRow.control_recurrence || evalRow.executed_next || evalRow.self_evaluation;

                          return (
                            <Card key={ctrl.controlId} withBorder radius="sm" p="md">
                              <Stack gap="xs">
                                <Group justify="space-between">
                                  <Text fw={500} size="sm">{ctrl.name}</Text>
                                  <Group gap="xs">
                                    <ActionIcon
                                      size="sm"
                                      variant={evaluated ? 'filled' : 'subtle'}
                                      color={evaluated ? 'green' : 'gray'}
                                    >
                                      {evaluated ? <IconCheck size={14} /> : <IconInfoCircle size={14} />}
                                    </ActionIcon>
                                    <ActionIcon
                                      size="sm"
                                      variant="subtle"
                                      color="blue"
                                      onClick={() => {
                                        setEditId(ctrl.controlId);
                                        setEditTxt(ctrl.name);
                                      }}
                                    >
                                      <IconEdit size={14} />
                                    </ActionIcon>
                                    <ActionIcon
                                      size="sm"
                                      variant="subtle"
                                      color="red"
                                      onClick={() => handleDelete(ctrl)}
                                    >
                                      <IconTrash size={14} />
                                    </ActionIcon>
                                  </Group>
                                </Group>
                                {ctrl.riskIds.length > 1 && (
                                  <Text size="xs" c="dimmed">
                                    Also mitigates {ctrl.riskIds.length - 1} other risk{ctrl.riskIds.length - 1 > 1 ? 's' : ''}
                                  </Text>
                                )}
                              </Stack>
                            </Card>
                          );
                        })}
                      </Stack>
                    </Accordion.Panel>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          )}
        </Stack>
      )}

      {/* Mitigation Efficiency Explanation Modal */}
      <AIScoreExplanationModal
        opened={explanationModal.opened}
        onClose={() => setExplanationModal({ opened: false, scoreType: null, scoreData: null })}
        scoreType={explanationModal.scoreType}
        scoreData={explanationModal.scoreData}
      />
    </Stack>
  );
}

export default ActionPlan;
